import { cloneDeep } from 'lodash';

const columnSettings = {
  name: {
    class: 'tablesort-sortable',
    stSort: 'name',
    displayName: 'TARGETS_NAME'
  },
  callType: {
    class: 'tablesort-sortable width-60',
    stSort: 'instructions.callType',
    displayName: 'TARGETS_TYPE'
  },
  destination: {
    class: 'tablesort-sortable',
    stSort: 'instructions.number',
    displayName: 'TARGETS_DESTINATION'
  },
  currentLive: {
    class: 'tablesort-sortable text-left p-l-15',
    stSort: 'usageStats.currentLive',
    displayName: 'STATS_LIVE',
    rowAlign: 'left'
  },
  currentHour: {
    class: 'tablesort-sortable text-left p-l-15',
    stSort: 'usageStats.currentHour',
    displayName: 'STATS_HOUR',
    rowAlign: 'left'
  },
  currentDay: {
    class: 'tablesort-sortable text-left p-l-15',
    stSort: 'usageStats.currentDay',
    displayName: 'STATS_DAY',
    rowAlign: 'left'
  },
  currentMonth: {
    class: 'tablesort-sortable text-left p-l-15',
    stSort: 'usageStats.currentMonth',
    displayName: 'STATS_MONTH',
    rowAlign: 'left'
  },
  total: {
    class: 'tablesort-sortable text-left p-l-15',
    stSort: 'usageStats.total',
    displayName: 'STATS_TOTAL',
    rowAlign: 'left'
  },
  status: {
    class: 'tablesort-sortable',
    stSort: 'tStatus.uiStatus.status',
    displayName: 'STATUS_TEXT',
    rowAlign: 'center'
  },
  owner: {
    class: 'tablesort-sortable width-15-percent',
    stSort: 'owner.name',
    displayName: 'TARGETS_OWNER',
    rowAlign: 'left',
    hideForUserType: ['buyer']
  },
  enabled: {
    class: 'tablesort-sortable',
    stSort: 'enabled',
    displayName: 'ENABLED_TEXT'
  },
  highRateCost: {
    class: 'tablesort-sortable',
    stSort: 'highRateCost',
    displayName: 'TARGETS_HIGH_RATE',
    hideForUserType: ['buyer', 'buyer_manager']
  }
};

const defaultColumns = [
  'name',
  'owner',
  'callType',
  'destination',
  'highRateCost',
  'currentLive',
  'currentHour',
  'currentDay',
  'currentMonth',
  'total',
  'status'
];

const callTypes = [{
  id: 'number',
  name: 'Number'
}, {
  id: 'sip',
  name: 'SIP'
}];


const targetCapTypes = [
  {
    name: 'Conversion',
    value: 'onConvert'
  },
  {
    name: 'Connection',
    value: 'onConnect'
  },
  {
    name: 'Revenue',
    value: 'onRevenue'
  }
];

const blockReasonTranslation = {
  'OPEN': {
    className: 'green_status',
    tooltip: 'TARGETS_STATUS_OPEN',
    status: 'open',
  },
  'CLOSED': {
    className: 'red_status',
    tooltip: 'TARGETS_STATUS_DISABLED',
    status: 'red-disabled',
  },
  'ConcurrencyHit': {
    className: 'yellow_status',
    tooltip: 'TARGETS_STATUS_CC_HIT',
    status: 'capped',
  },
  'GroupConcurrencyHit': {
    className: 'yellow_status',
    tooltip: 'TARGETS_STATUS_CC_HIT_GROUP',
    status: 'capped',
  },
  'IsCapped': {
    className: 'yellow_status',
    tooltip: 'TARGETS_STATUS_CAPPED',
    status: 'capped',
  },
  'GroupIsCapped': {
    className: 'yellow_status',
    tooltip: 'TARGETS_STATUS_CAPPED_GROUP',
    status: 'capped',
  },
  'IsClosed': {
    className: 'red_status',
    tooltip: 'TARGETS_STATUS_CLOSED',
    status: 'red-disabled',
  },
  'GroupIsClosed': {
    className: 'red_status',
    tooltip: 'TARGETS_STATUS_CLOSED_GROUP',
    status: 'red-disabled',
  },
  'Disabled': {
    className: 'red_status',
    tooltip: 'TARGETS_STATUS_DISABLED',
    status: 'red-disabled',
  },
  'GroupDisabled': {
    className: 'red_status',
    tooltip: 'TARGETS_STATUS_DISABLED_GROUP',
    status: 'red-disabled',
  },
  'BuyerConcurrencyHit': {
    className: 'yellow_status',
    tooltip: 'BUYERS_STATUS_CC_HIT',
    status: 'capped',
  },
  'BuyerIsCapped': {
    className: 'yellow_status',
    tooltip: 'BUYERS_STATUS_CAPPED',
    status: 'capped',
  },
};

export default class TargetService {
  /** @ngInject **/
  constructor(
    _,
    $http,
    concurrencyMatrixService,
    ngAuthSettings,
    promiseCacheService,
    utils,
    statsService
  ) {
    this._ = _;
    this.$http = $http;
    this.concurrencyMatrixService = concurrencyMatrixService;
    this.utils = utils;
    this.promiseCacheService = promiseCacheService;
    this.serviceBaseUri = ngAuthSettings.apiServiceBaseUri;
    this.columnSettings = cloneDeep(columnSettings);
    this.defaultColumns = cloneDeep(defaultColumns);
    this.blockReasonTranslation = cloneDeep(blockReasonTranslation);
    this.callTypes = callTypes;
    this.targetCapTypes = targetCapTypes;
    this.statsService = statsService;
  }

  initNewTarget() {
    return {
      instructions: {
        callType: 'number'
      },
      targetCallIncrement: 'onConvert',
      isCriteriaInverted: false
    };
  }

  getTargets(accountId, getStats, updateStatsObj) {
    const url = this.serviceBaseUri + accountId + '/targets';
    let params = {};
    if (getStats) {
      params = { includeStats: true };
    }
    return this.$http({
      url: url,
      method: 'GET',
      params: params
    }).then((response) => {
      let targets;
      if (updateStatsObj) {
        targets = updateStatsObj.targets;
      } else {
        targets = response.data.targets;
      }

      if (getStats) {
        this.statsService.normalizeStats(targets, response.data.stats, response.data.caps, blockReasonTranslation);
      }

      this.#patchHighRateCostTargetArray(targets);
      return targets;
    });
  }

  getCachedTargets(accountId) {
    const url = this.serviceBaseUri + accountId + '/targets';
    return this.promiseCacheService.getCallWithCachedPromise(this.#getCachedKey(accountId), url, 30);
  }

  getTarget(accountId, targetId) {
    const url = this.#serviceBaseUrl(accountId, targetId);
    return this.$http.get(url).then((response) => {
      if (response && response.data && response.data.target) {
        this.#patchHighRateCostTarget(response.data.target);
      }

      return response;
    });
  }

  createTarget(accountId, target) {
    const url = this.serviceBaseUri + accountId + '/targets';
    this.#setDefaults(target);
    if (target.schedule.callConcurrencyMatrix) {
      target.schedule.callConcurrencyMatrix = this.concurrencyMatrixService.postProcessMatrix(target.schedule.callConcurrencyMatrix);
    }
    if (target.schedule.callCapMatrix) {
      target.schedule.callCapMatrix = this.concurrencyMatrixService.postProcessMatrix(target.schedule.callCapMatrix);
    }
    return this.$http.post(url, target);
  }

  checkIfDuplicate(accountId, target) {
    const url = this.serviceBaseUri + accountId + '/targets/isDuplicate';
    return this.$http.post(url, target);
  }

  updateTarget(accountId, target) {
    const sanitazedTarget = this.#sanitizeTarget(target);
    const url = this.#serviceBaseUrl(accountId, sanitazedTarget.id);
    this.#setDefaults(target);
    const updatedTarget = this.#removeUnPatchableItems(sanitazedTarget);
    if (updatedTarget.schedule.callConcurrencyMatrix) {
      updatedTarget.schedule.callConcurrencyMatrix = this.concurrencyMatrixService.postProcessMatrix(updatedTarget.schedule.callConcurrencyMatrix);
    }
    if (updatedTarget.schedule.callCapMatrix) {
      updatedTarget.schedule.callCapMatrix = this.concurrencyMatrixService.postProcessMatrix(updatedTarget.schedule.callCapMatrix);
    }
    return this.$http.patch(url, updatedTarget);
  }

  deleteTarget(accountId, targetId, force) {
    const url = this.#serviceBaseUrl(accountId, targetId) + '?force=' + !!force;
    return this.$http.delete(url);
  }

  getTargetReferences(accountId, targetId) {
    const url = this.#serviceBaseUrl(accountId, targetId) + '/InboundReferences';
    return this.$http.get(url);
  }

  updateTargetProperty(accountId, targetId, item) {
    const url = this.#serviceBaseUrl(accountId, targetId);
    const updatedItem = this.#removeUnPatchableItems(item);
    if (updatedItem.schedule && updatedItem.schedule.callConcurrencyMatrix) {
      updatedItem.schedule.callConcurrencyMatrix = this.concurrencyMatrixService.postProcessMatrix(updatedItem.schedule.callConcurrencyMatrix);
    }
    if (updatedItem.schedule && updatedItem.schedule.callCapMatrix) {
      updatedItem.schedule.callCapMatrix = this.concurrencyMatrixService.postProcessMatrix(updatedItem.schedule.callCapMatrix);
    }
    return this.$http.patch(url, updatedItem);
  }

  resetCallCounters(accountId, targetId) {
    const url = this.#serviceBaseUrl(accountId, targetId) + '/resetcount';
    return this.$http.delete(url);
  }

  buyerToggleTargetEnabled(accountId, targetId) {
    const url = this.#serviceBaseUrl(accountId, targetId) + '/buyertogglepause';
    return this.$http.put(url);
  }

  buyerSetConcurrencyCap(accountId, targetId, data) {
    const url = this.#serviceBaseUrl(accountId, targetId) + '/buyersetcaps';
    if (data.callConcurrencyMatrix) {
      data.callConcurrencyMatrix = this.concurrencyMatrixService.postProcessMatrix(data.callConcurrencyMatrix);
    }
    if (data.callCapMatrix) {
      data.callCapMatrix = this.concurrencyMatrixService.postProcessMatrix(data.callCapMatrix);
    }
    return this.$http.put(url, data);
  }

  duplicateTarget(accountId, targetId) {
    const url = this.#serviceBaseUrl(accountId, targetId);
    return this.$http.get(url).then((response) => {
      if (response && response.data && response.data.target) {
        const target = response.data.target;

        this.#patchHighRateCostTarget(target);

        target.id = null;
        target.instructions = {
          callType: 'number'
        };

        target.name += ' - Copy';

        // Duplicate target should not be in any group
        target.targetGroupId = '';

        // Set a flag on the target for use in the controller
        target.isDuplicate = true;

        return target;
      }

      return null;
    });
  }

  validateTargetNumber(accountId, number) {
    const url = this.serviceBaseUri + accountId + '/targets/validateNumber/' + number;
    return this.$http.get(url);
  }

  linkToItem(accountId, targetId, item) {
    const url = this.#serviceBaseUrl(accountId, targetId) + '/link';
    return this.$http.patch(url, item);
  }

  unlinkItem(accountId, targetId, item) {
    const url = this.#serviceBaseUrl(accountId, targetId) + '/unlink';
    return this.$http.patch(url, item);
  }

  #removeUnPatchableItems(target) {
    //owner can not be patched
    const newTarget = angular.copy(target);
    if (typeof newTarget.owner !== 'undefined') {
      delete newTarget.owner;
    }
    // High rate params cannot be patched by the user
    if (typeof newTarget.highRateCPM !== 'undefined') {
      delete newTarget.highRateCPM;
    }
    if (typeof newTarget.highRateCost !== 'undefined') {
      delete newTarget.highRateCost;
    }
    if (typeof newTarget.isHighRateTarget !== 'undefined') {
      delete newTarget.isHighRateTarget;
    }
    return newTarget;
  }

  #setDefaults(target) {
    target.instructions = target.instructions || {};
    target.instructions.connectionTimeOut = target.instructions.connectionTimeOut || 0;
  }

  #serviceBaseUrl(accountId, targetId) {
    return this.serviceBaseUri + accountId + '/targets/' + targetId;
  }

  #patchHighRateCostTargetArray(targets) {
    this._.forEach(targets, (t) => {
      this.#patchHighRateCostTarget(t);
    });
  }

  #patchHighRateCostTarget(target) {
    target.highRateCost = (target.highRateCPM != undefined ? target.highRateCPM : target.highRateCost) || 0.0;
  }

  #getCachedKey(accountId) {
    return 'targetsgetPromise' + accountId;
  }

  #sanitizeTarget(target) {
    const sanitizedTarget = angular.copy(target);

    delete sanitizedTarget.showEditForm;

    sanitizedTarget.usageStats = undefined;
    sanitizedTarget.tStatus = undefined;
    sanitizedTarget.caps = undefined;

    return sanitizedTarget;
  }

  createMatrix(initial) {
    const matrix = new Array();
    for (let day = 0; day < 7; day++) {
      matrix.push(new Array());
      for (let hour = 0; hour < 24; hour++) {
        matrix[day].push(initial);
      }
    }
    return matrix;
  }

  newEmptyMatrix() {
    return this.createMatrix(undefined);
  }

  cloneMatrix(source) {
    const matrix = new Array();
    if (source) {
      for (let day = 0; day < 7; day++) {
        matrix.push(new Array());
        for (let hour = 0; hour < 24; hour++) {
          matrix[day].push(source[day][hour]);
        }
      }
    }
    return matrix;
  }

}
